import { useNavigate } from 'react-router';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { rememberChosenPeriod, resolveDefaultPeriodId } from '@/core/periodDefault';
import { phase2Service } from '@/core/services/phase2';
import { practiceService } from '@/core/services/practice';
import { reportsService } from '@/core/services/reports';
import type { AccountsBuilderScreen, OutputProfileRow } from '@/core/models/phase2.models';
import type { PeriodRow } from '@/core/models/practice.models';
import type { ReportRow } from '@/core/models/reports.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { Switch } from '@/kit/Switch';
import { Tooltip } from '@/kit/Tooltip';
import { NO_PERIOD_PLACEHOLDER } from '@/core/format/periodLabel';
import { NoPeriodNote, periodTo, useAlive, useClientInit, useLive } from './common';
import './TaxreturnAccountsbuilder.scss';

/** APT355: "AA-09 - description"; a report with no description is just its reference, and an empty item (no reports) is empty, never "undefined". */
const reportLabel = (item: ReportRow): string => [item?.refNo, item?.description].filter(Boolean).join(' - ');

interface Model {
  periods: PeriodRow[];
  /** APT355: the client's periods have been read (so "no accounting period" is only said once it is known). */
  periodsLoaded: boolean;
  periodId: string;
  screen: AccountsBuilderScreen | null;
  profiles: OutputProfileRow[];
  /** `profileCode` of the profile shown on the right (Angular held the row object itself). */
  selectedCode: string | null;
  reports: ReportRow[];
  reportId: string;
  clientType: number | null;
  /** APT251: the preparer clicked an output, so a reload keeps it rather than following the selected report. */
  pickedByUser: boolean;
  /** Save, Produce accounts or Print is running (stops a second click). */
  busy: boolean;
}

/**
 * APT251: the output each Report Type produces - a report's accountsVariant (ReportRow.typeId: 0 Full, 1 Abridged,
 * 2 Filleted). The API says it on each output (`reportVariant`); these codes are the fallback for an older API.
 */
const OUTPUT_FOR_REPORT_VARIANT: Record<number, readonly string[]> = {
  0: ['MEMBERS', 'FULL'],
  1: ['ABRIDGED'],
  2: ['FILLETED'],
};

/** AP-009: a page's inclusion state (0 Required, 1 Optional, 2 Omission permitted, 3 Not permitted) as a pill colour. */
const INCLUSION_PILL_CLASS: Record<number, string> = {
  0: 'ab-pill--required',
  1: 'ab-pill--optional',
  2: 'ab-pill--omission',
  3: 'ab-pill--blocked',
};

/**
 * APT251: "even if I select full accounts it shows Abridged". The builder opened on the first output in the list
 * whatever report was selected; it now opens on the output that produces the selected report's type.
 */
export function profileForReport(
  profiles: OutputProfileRow[],
  report: Pick<ReportRow, 'typeId'> | null | undefined,
): OutputProfileRow | null {
  const variant = report?.typeId;
  if (typeof variant !== 'number') return null;
  const codes = OUTPUT_FOR_REPORT_VARIANT[variant] ?? [];
  return (
    profiles.find((p) => p.reportVariant === variant) ??
    profiles.find((p) => codes.includes(p.profileCode.toUpperCase())) ??
    null
  );
}

/**
 * item 136: Accounts Builder output profiles and pages should not display "Members"
 * terminology for companies (Limited Company, etc.).
 * In UK accounting, LLPs have members, while companies have shareholders and directors.
 */
export function checkIsLlp(screen: AccountsBuilderScreen | null, clientType: number | null): boolean {
  if (clientType === 3) return true;
  if (clientType === 0) return false;
  const fw = screen?.frameworkCode?.toUpperCase();
  const at = screen?.accountsTypeCode?.toUpperCase();
  return fw === 'LLP' || at === 'LLP';
}

/** "members" -> "shareholders", keeping the capital: a company's members are its shareholders. */
function toShareholders(text: string | null): string | null {
  return text
    ? text.replace(/\b(M|m)embers\b/g, (_match, m: string) => (m === 'M' ? 'Shareholders' : 'shareholders'))
    : text;
}

export function sanitizeProfiles(allProfiles: OutputProfileRow[], isLlp: boolean): OutputProfileRow[] {
  // APT341: the Abridged output is never offered (an API that predates the change may still send it for a period that had it switched on).
  const profiles = allProfiles.filter((p) => (p.profileCode ?? '').toUpperCase() !== 'ABRIDGED');
  if (isLlp) return profiles;

  // APT251: this used to drop every output whose code contains "member" - and the API's code for the full accounts is
  // MEMBERS, so a company lost its Full accounts output and the builder opened on Abridged. The output is kept and,
  // like the API already does for a company, named "Full accounts".
  return profiles.map((p) => {
    const isFull = p.profileCode === 'FULL' || p.profileCode === 'MEMBERS';
    let name = p.name;
    if (isFull || /\bmembers\b/i.test(name)) {
      name = name
        .replace(/\bMembers\s*\/?\s*\(?full\)?/gi, 'Full accounts')
        .replace(/\bFull\s*\(members\)\b/gi, 'Full accounts')
        .replace(/\bMembers\s*\/\s*Full\b/gi, 'Full accounts')
        .replace(/\bMembers\b/gi, isFull ? 'Full accounts' : 'Shareholders');
    }

    // item 136 / APT251: a company never reads "members". A company's members are its shareholders (CA 2006 s.112), so
    // the description and each page's reason say "shareholders" and keep their CA 2006 citation. The reasons used to
    // be rewritten to "directors" ("The profit and loss account must be sent to the directors."), which s.423 does not
    // say. The API words them this way itself; this is the same rule for an API that does not yet, and changes
    // nothing once it does. A sole trader's or partnership's reasons come from the API without "members".
    const description = toShareholders(p.description);

    const pages = p.pages.map((page) => {
      let pageName = page.pageName;
      if (/\bmembers\b/i.test(pageName)) {
        pageName = pageName
          .replace(/\bMembers'\s*report\b/gi, "Directors' report")
          .replace(/\bMember's\s*report\b/gi, "Director's report")
          .replace(/\bReport\s+of\s+the\s+members\b/gi, 'Report of the directors')
          .replace(/\bMembers'\s*responsibilit/gi, "Directors' responsibilit")
          .replace(/\bApproval\s+by\s+members\b/gi, 'Approval by directors')
          .replace(/\bMembers'\s*approval\b/gi, "Directors' approval")
          .replace(/\bMembers\b/g, 'Directors')
          .replace(/\bmembers\b/g, 'directors');
      }

      return {
        ...page,
        pageName,
        reason: toShareholders(page.reason),
      };
    });

    return {
      ...p,
      name,
      description,
      pages,
    };
  });
}

/**
 * The accounts builder and its output profiles (AP-009): Members/Full, Abridged, Filing/Filleted, HMRC/Tax,
 * Management and Detailed P&L, all produced from the one approved dataset. Pages a rule fixes in or out cannot be
 * changed, so a filleted copy is produced by rule and never by deleting pages; a profile the period is not eligible
 * for shows the blocking rule and a link that fixes it. (Angular TaxreturnAccountsbuilderComponent.)
 */
export function TaxreturnAccountsbuilder() {
  const alive = useAlive();
  const navigate = useNavigate();
  const [m, set, ref] = useLive<Model>({
    periods: [],
    periodsLoaded: false,
    periodId: '',
    screen: null,
    profiles: [],
    selectedCode: null,
    reports: [],
    reportId: '',
    clientType: null,
    pickedByUser: false,
    busy: false,
  });

  const selectedOf = (s: Model) => s.profiles.find((p) => p.profileCode === s.selectedCode) ?? null;
  const selected = selectedOf(m);
  const reportOf = (s: Model) => s.reports.find((r) => r.id === s.reportId) ?? null;

  /**
   * APT251: which output to show after the outputs (re)load: the one the preparer clicked, while it still exists;
   * otherwise the output the selected report is (Full, Abridged or Filleted); otherwise the one already shown, then the
   * first.
   */
  const nextSelection = (s: Model, profiles: OutputProfileRow[]): OutputProfileRow | null => {
    const current = selectedOf(s)?.profileCode;
    const kept = profiles.find((p) => p.profileCode === current);
    if (s.pickedByUser && kept) return kept;
    return profileForReport(profiles, reportOf(s)) ?? kept ?? profiles[0] ?? null;
  };

  const load = async () => {
    const periodId = ref.current.periodId;
    if (!periodId) {
      return;
    }
    try {
      const screen = await phase2Service.getOutputProfiles(periodId);
      if (!alive.current) return;
      const isLlp = checkIsLlp(screen, ref.current.clientType);
      const profiles = sanitizeProfiles(screen.profiles ?? [], isLlp);
      const next = nextSelection(ref.current, profiles);
      set({ screen, profiles, selectedCode: next ? next.profileCode : null });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  /** APT251: choosing a report shows the output that produces it (the reports can arrive after the outputs). */
  const followReport = () => {
    const s = ref.current;
    if (s.pickedByUser) return;
    const match = profileForReport(s.profiles, reportOf(s));
    if (match) set({ selectedCode: match.profileCode });
  };

  useClientInit((client) => {
    if (typeof practiceService.getClient === 'function') {
      practiceService
        .getClient(client.id)
        ?.then((detail) => {
          if (!alive.current) return;
          set({ clientType: detail?.clientType ?? null });
        })
        .catch(() => undefined);
    }

    reportsService
      .getReports(client.id, 0)
      .then((rows) => {
        if (!alive.current) return;
        const reports = rows ?? [];
        set({ reports, ...(reports.length > 0 ? { reportId: reports[0].id } : {}) });
        followReport();
      })
      .catch((error) => {
        if (alive.current) notifyError(error);
      });

    practiceService
      .getClientPeriods(client.id)
      .then(async (result) => {
        if (!alive.current) return;
        const periods = result.periods ?? [];
        if (periods.length > 0) {
          // The period the user last chose, else the latest with data, else the latest that has ended (see pickDefaultPeriodId).
          set({ periods, periodsLoaded: true, periodId: (await resolveDefaultPeriodId(client.id, periods)) ?? periods[0].id });
          await load();
        } else {
          set({ periods, periodsLoaded: true });
        }
      })
      .catch((error) => {
        if (alive.current) notifyError(error);
      });
  });

  /** Follows the blocking rule's link to the screen that makes the profile eligible. */
  const fix = (route: string | null) => {
    if (route) {
      navigate(route.split('?')[0]);
    }
  };

  /** Where an Annual Accounts report (the "Report" select's rows) is created. */
  const openReportsAccounts = () => void navigate('/home/taxreturns/reports-accounts');

  /** The loaded profile objects were edited in place in Angular; here the profile is replaced. */
  const editProfile = (profileCode: string, edit: (profile: OutputProfileRow) => OutputProfileRow) => {
    set({ profiles: ref.current.profiles.map((p) => (p.profileCode === profileCode ? edit(p) : p)) });
  };

  const save = async () => {
    const current = selectedOf(ref.current);
    if (!current || ref.current.busy) {
      return;
    }

    set({ busy: true });
    try {
      const screen = await phase2Service.saveOutputProfile(
        {
          accountingPeriodId: ref.current.periodId,
          profileCode: current.profileCode,
          isEnabled: current.isEnabled,
          pages: current.pages.map((page) => ({ pageId: page.pageId, include: page.include })),
        },
        current.eTag,
      );
      if (!alive.current) return;
      const isLlp = checkIsLlp(screen, ref.current.clientType);
      const profiles = sanitizeProfiles(screen.profiles ?? [], isLlp);
      const next = profiles.find((p) => p.profileCode === selectedOf(ref.current)?.profileCode) ?? null;
      set({ screen, profiles, selectedCode: next ? next.profileCode : null });
      notifySuccess('The output profile has been saved.');
    } catch (error) {
      if (alive.current) notifyError(error);
    } finally {
      if (alive.current) set({ busy: false });
    }
  };

  const generate = async (isPrint: boolean) => {
    const current = selectedOf(ref.current);
    if (!current) {
      return;
    }
    if (!ref.current.reportId) {
      notifyError('Select the accounts report whose trial balances supply the figures.');
      return;
    }
    if (ref.current.busy) {
      return;
    }

    set({ busy: true });
    try {
      await phase2Service.generateAccounts({
        accountingPeriodId: ref.current.periodId,
        profileCode: current.profileCode,
        reportId: ref.current.reportId,
        isPrint,
      });
      if (!alive.current) return;
      void load();
    } catch (error) {
      if (alive.current) notifyError(error);
    } finally {
      if (alive.current) set({ busy: false });
    }
  };

  const isLocked = m.screen?.isLocked;
  // APT251: a blocked output cannot be switched on, so the API refuses to save it while it is ticked. Say so beside
  // Save instead of letting Save fail, and keep the pages saveable once it is unticked.
  const blocked = !!selected && !selected.isEligible;
  const cannotSave = blocked && !!selected?.isEnabled;

  return (
    <SideMenuPage menu="task">
      <div className="content-body taxreturn-accountsbuilder">
        {/* The outputs tab strip already names the page ("Accounts Builder" is the side menu's own label for it
            too), so the title is kept only as the page's accessible name, not shown. A `<PageHeader hideTitle>`
            still renders its sticky bar - border and padding included - with nothing visible inside it, which
            read as a stray blank strip above the tabs; a bare hidden heading (as Working Papers already uses)
            carries the same accessible name with no bar at all. */}
        <h2 className="cdk-visually-hidden">Accounts Builder</h2>

        {/* AP-009: the outputs (Full accounts, Companies House filing, …) are a horizontal tab strip rather than a
            sidebar, so the page only ever scrolls in one direction - not a long table scrolling past a pinned
            column of tabs. Sits above the filters/actions row, the same arrangement as the section nav bars (e.g.
            Compliance & Filing's own tab strip above its Validation Centre filters). The output's description used
            to repeat under the strip as a duplicate "Full accounts" label right below the already-selected tab; it
            now lives in an info icon beside the active tab instead. */}
        <nav className="accounts-builder-outputs" aria-label="Outputs">
          {/* APT251: each output is a real button, so it can be reached and chosen from the keyboard. */}
          {m.profiles.map((profile) => {
            const isActive = profile.profileCode === m.selectedCode;
            return (
              <button
                key={profile.profileCode}
                type="button"
                className={`output-tab${isActive ? ' active' : ''}`}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => set({ selectedCode: profile.profileCode, pickedByUser: true })}
              >
                {profile.name}
                {!profile.isEligible && <span className="output-flag"> (blocked)</span>}
                {profile.isStale && <span className="output-flag"> (out of date)</span>}
                {isActive && profile.description && (
                  <Tooltip title={profile.description}>
                    <Icon name="info" outlined className="accounts-builder-outputs__info-icon" />
                  </Tooltip>
                )}
              </button>
            );
          })}
        </nav>

        <div className="content-header mb-0">
          <div className="content-header-left mb-1 ab-filters">
            <Select
              className="w-250 mr-1"
              openOnFocus
              panelClass="myPanelClass"
              placeholder="Report"
              items={m.reports}
              bindLabel="refNo"
              bindValue="id"
              clearable={false}
              value={m.reportId || null}
              onChange={(value) => {
                set({ reportId: value as string, pickedByUser: false });
                followReport();
              }}
              renderLabel={reportLabel}
              renderOption={reportLabel}
            />
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              placeholder={NO_PERIOD_PLACEHOLDER}
              items={m.periods}
              bindLabel="fromDate"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.periodId || null}
              onChange={(value) => {
                set({ periodId: value as string });
                rememberChosenPeriod(value as string);
                void load();
              }}
              renderLabel={periodTo}
              renderOption={periodTo}
            />
          </div>
          {selected && (
            <div className="ab-produce-toggle">
              <Switch
                checked={selected.isEnabled}
                disabled={isLocked || (blocked && !selected.isEnabled)}
                onChange={(event) =>
                  editProfile(selected.profileCode, (p) => ({ ...p, isEnabled: event.target.checked }))
                }
              >
                Produce this output for the period
              </Switch>
            </div>
          )}
          <div className="content-header-right mb-1 ab-actions">
            {selected && (
              <>
                <Button
                  className="action-button primary-button"
                  busy={m.busy}
                  onClick={() => void save()}
                  disabled={isLocked || cannotSave}
                >
                  <Icon name="save" outlined />
                  <span>Save</span>
                </Button>
                <Button
                  className="action-button secondary-button"
                  busy={m.busy}
                  onClick={() => void generate(false)}
                  disabled={!selected.isEligible}
                >
                  <Icon name="description" outlined />
                  <span>Produce accounts</span>
                </Button>
                <Button className="action-button secondary-button" busy={m.busy} onClick={() => void generate(true)} disabled={!selected.isEligible}>
                  <Icon name="print" outlined />
                  <span>Print</span>
                </Button>
              </>
            )}
          </div>
        </div>
        <NoPeriodNote show={m.periodsLoaded && m.periods.length === 0} />
        {/* The "Report" select has nothing to offer until an Annual Accounts report exists for the client - say so
            and where to make one, rather than letting Produce accounts/Print fail with no way forward from here. */}
        {m.periodId && m.reports.length === 0 && (
          <p className="text-muted font-size-12 mb-1">
            There is no accounts report yet to supply the trial balance figures. Create one under{' '}
            <a className="link" onClick={openReportsAccounts}>
              Reports &gt; Annual Accounts
            </a>
            , then pick it here as the Report before producing or printing.
          </p>
        )}

        {selected && (
          <div className="accounts-builder-detail">
            {selected.blockers.length > 0 && (
              <div>
                <p>This output cannot be produced for this period:</p>
                <table className="wp-100">
                  <tbody>
                    {selected.blockers.map((blocker, index) => (
                      <tr key={`${blocker.ruleCode}-${index}`}>
                        <td>{blocker.ruleCode}</td>
                        <td>{blocker.message}</td>
                        <td>{blocker.sourceReference}</td>
                        <td>
                          {blocker.fixRoute && (
                            <Button title="Fix" onClick={() => fix(blocker.fixRoute)}>
                              <Icon name="build" outlined />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* AP-009: State moved into its own column between Page and Rule (it used to share the Rule column),
                and Include moved to the right-hand edge so the toggle always lines up on the same side.
                `.mat-table-wrapper` is the app's own "a list sits in a bordered, rounded white card" convention
                (see Statutory Database) - a plain table outside it just sits on the page's own grey background. */}
            <div className="mat-table-wrapper mt-1">
            <table className="wp-100 accounts-builder-pages">
              <tbody>
                <tr>
                  <th>Page</th>
                  <th className="col-state">State</th>
                  <th>Rule</th>
                  <th>Why</th>
                  <th className="col-include">Include</th>
                </tr>
                {selected.pages.map((page) => (
                  <tr key={page.pageId}>
                    <td>
                      {page.pageName}
                      {/* item 137: where the tick came from, so a preparer can see why this page differs from
                          Report Settings. An API older than item 137 doesn't say, and then nothing is shown. */}
                      {page.includeSource && (
                        <>
                          <br />
                          <small>Starting point: {page.includeSourceName}</small>
                        </>
                      )}
                    </td>
                    <td className="col-state">
                      <span className={`ab-pill ${INCLUSION_PILL_CLASS[page.inclusion] ?? 'ab-pill--optional'}`}>
                        {page.inclusionName}
                      </span>
                    </td>
                    <td>{page.ruleCode}</td>
                    <td>
                      {page.reason}
                      <br />
                      <small>{page.sourceReference}</small>
                    </td>
                    <td className="col-include">
                      <Switch
                        checked={page.include}
                        disabled={!page.canChange || isLocked}
                        showLock={!page.canChange}
                        onChange={(event) =>
                          editProfile(selected.profileCode, (p) => ({
                            ...p,
                            pages: p.pages.map((pg) =>
                              pg.pageId === page.pageId ? { ...pg, include: event.target.checked } : pg,
                            ),
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {isLocked && (
              <p className="accounts-builder-note" role="note">
                Save is unavailable: the period is finalised, so its outputs can no longer be changed.
              </p>
            )}
            {!isLocked && cannotSave && (
              <p className="accounts-builder-note" role="note">
                Save is unavailable: this output is blocked for this period by the rule above, so it cannot be
                switched on. Untick &ldquo;Produce this output for the period&rdquo; to save the page choices only, or
                fix the rule.
              </p>
            )}
            {blocked && (
              <p className="accounts-builder-note" role="note">
                Produce accounts and Print are unavailable until the rule above no longer blocks this output.
              </p>
            )}
          </div>
        )}
      </div>

    </SideMenuPage>
  );
}
